import { tt } from "../i18n/tt";
import { lazyI18n } from "../i18n/lazyText";
// 크리에이터 등급 명칭 공용 상수.
//
// 정책상 등급 밴드는 상대 컷(상위 10/30/60/85%)이고, "마스터/다이아몬드/플래티넘/골드/실버"라는
// 게임식 명칭은 2026-08-28 회의에서 임시로 합의된 뒤 **2026-08-29 FRONTEND-HANDOFF.md 결정으로
// 확정됐다**(더 이상 미확정 아님 — ALGORITHM-SPEC-v15.md 6절). 명칭이 다시 바뀌면 이 배열만
// 고치면 MyTier(크리에이터), AdminCreatorTiers(어드민), 제재 강등 미리보기, 등급 체계 안내
// 팝오버(MyTier.jsx `TierGuidePopover`, 2026-08-30 신규) 문구가 전부 함께 바뀐다.
//
// 낮은 등급 → 높은 등급 순.
export const CREATOR_TIER_NAMES = ["실버", "골드", "플래티넘", "다이아몬드", "마스터"];

// 2026-09-18 — 등급제 "절대평가 전환"(v16, ALGORITHM-SPEC-v16-ABSOLUTE.md §3) 반영: 마스터만
// 여전히 상대 컷(다이아 인원과 무관한 별도 특수 등급)이고, 다이아몬드~실버 네
// 등급은 더 이상 "상위 30/60/85%" 같은 백분위 컷이 아니라 **전환일에 굳힌 고정 점수 컷**이다
// (매월 재확정되지만 그 달 안에서는 상수 — `creator_tier_config.cut_a/b/c`, 크리에이터 화면
// 노출용은 `creator_tier_daily_stats.band_cut_a/b/c`). 예전 S~D 백분위 매핑 주석은 더 이상
// 사실이 아니라 삭제했다 — 아래 값은 배지 옆에 붙는 짧은 보조 라벨(LadderCard 그리드, 등급
// 체계 안내 팝오버)용이고, 실제 컷 수치는 여기서 새로 짓지 않는다(수치는 각 화면이 API 값을
// 그대로 읽는다).
//
// 2026-09-21 — v17 초안(회의 결정, `projects/creator-tier-system/ALGORITHM-SPEC-v17-DRAFT.md` §0)
// 반영: 마스터 자격 비율을 **상위 10% → 상위 20%**로 조정하고, "다이아 컷 이상인 사람 중"이라는
// 조건이 새로 붙었다(다이아 컷을 못 넘으면 20% 안에 들어도 마스터가 아니다 — 다이아 0명도 가능).
// 크리에이터 화면 문구는 전부 `MASTER_TOP_RATIO` 하나에서 만든다 — 비율이 다시 바뀌면 여기만
// 고치면 된다. 어드민 「산정 모드」 패널(`TierModePanel.jsx`)은 이 상수를 쓰지 않고 서버 실측값
// (`creator_tier_config.master_ratio`)을 그대로 보여준다 — 운영 중에 값을 조정할 수 있어서다.
export const MASTER_TOP_RATIO = 20;

export const CREATOR_TIER_CUT_LABELS = lazyI18n(() => ({
  마스터: tt("a.tier.masterTopRatioLabel", "상위 {{v0}}%", { v0: MASTER_TOP_RATIO }),
  다이아몬드: tt("a.tier.fixedCut", "고정 컷"),
  플래티넘: tt("a.tier.fixedCut", "고정 컷"),
  골드: tt("a.tier.fixedCut", "고정 컷"),
  실버: tt("a.9aa01512", "그 외"),
}));

// 정식 등급 중 가장 높은 등급(마스터) — "상위 N% 특수 등급" 문구·판정에서 매직 스트링 대신 쓴다.
export const MASTER_TIER_NAME = CREATOR_TIER_NAMES[CREATOR_TIER_NAMES.length - 1];

// 루키(작품 연결 영상 5개 미만) — 정식 등급(S~D/실버~마스터)에 포함되지 않는 별도 상태.
// ALGORITHM-SPEC-v15.md 2절 참고.
export const ROOKIE_LABEL = "루키"; // 데이터 키(등급명) — 화면에서는 ttl(ROOKIE_LABEL)
export const ROOKIE_VIDEO_THRESHOLD = 5;

// 휴면(90일 무활동) — 등급 보존 + 배정 후보 제외. ALGORITHM-SPEC-v15.md 6절 참고.
export const DORMANT_LABEL = "휴면"; // 데이터 키(등급명) — 화면에서는 ttl(DORMANT_LABEL)
export const DORMANT_INACTIVE_DAYS = 90;

// 2026-09-02 신규 → 같은 날 정정(사용자 확정) — 휴면 표시 정책(`fix/dormant-tier-hidden`
// 브랜치). DB는 등급을 계속 동결 저장하지만(band 컬럼은 그대로 보존), **표시 계층에서는
// 크리에이터 본인·권리사 화면 모두 동결 등급(이름·심볼·백분위)을 절대 보여주지 않는다.**
// 근거: 동결 등급을 보여주면 "복귀하면 그 등급부터 다시 시작"이라는 잘못된 기대가 생기지만,
// 실제로는 복귀해도 그 등급으로 돌아가는 게 아니라 최근 활동 기준으로 완전히 새로 재산정된다
// (감쇠 적용, 유예 없음) — 등급을 숨기면 오히려 "재시작"이라는 정확한 개념으로 읽힌다.
//
// **정정(사용자 확정)** — 첫 구현이 dormant 카피 전체를 이 문구 하나로 뭉뚱그리면서
// **복귀 조건 안내("작품 영상 1편 또는 검수 1건이면 다음 날부터 배정 후보에 다시 들어간다")를
// 실수로 지웠다.** 이 안내는 실제 판정 로직(작품 영상 `published_at`·검수 `created_at` 기준
// 90일 무활동으로 휴면 진입, 하나만 생기면 다음 일별 계산(매일 07:00 KST 크론)에서 즉시 해제)과
// 정확히 일치하는 사실 정보라 등급 노출 문제와 무관하다 — 그래서 삭제 대상이 아니라 보존
// 대상이다. 이 상수 자체는 "지금 상태 + 재계산 원칙"만 담고, **구체적인 복귀 조건 문장은
// 이 상수를 쓰는 각 화면이 별도 문장으로 이어붙인다**(아래 `src/lib/tierCardCopy.js`의
// "dormant" 케이스, `MyTier.jsx`, `CreatorTierDetailPanel.jsx` 참고 — 전부 "재계산 원칙 +
// 복귀 조건" 두 문장 구조를 공유한다). 어드민(`AdminCreatorTiers.jsx`)은 이 정책의 예외 —
// 운영 목적상 동결 등급을 "마지막 등급"으로 계속 보여준다.
export const DORMANT_STATUS_HINT =
  "휴면 상태예요 — 업로드를 재개하면 최근 활동 기준으로 등급이 다시 계산돼요";

export function tierIndex(tierName) {
  return CREATOR_TIER_NAMES.indexOf(tierName);
}

// 2026-08-30 밤 신규(사용자 지시) → 같은 날 밤 재확정(사용자 재지시) → 같은 날 밤 마감 품질
// 수정(사용자 확정, 상승 케이스 스크린샷 검수) — MyTier.jsx
// `DailyTrendSparkline`의 등급 구역 배경과 일별 데이터 포인트 색을 **한 곳에서 관리**하기 위한
// 공용 팔레트. `zone`은 배경 구역용(아주 옅게), `point`는 그 구역 안에 놓인 데이터 포인트/선
// 색(더 진하게) — **포인트 색은 그 점의 추상적인 등급명이 아니라, 차트에 실제로 그려진 구역 중
// 어디에 그 점이 위치하는지로 정해진다**(재확정 사항 — 처음엔 그날의 band 값을 그대로 매핑
// 했었는데, 이 구역 곡선이 안 그려진 등급(예: 다이아몬드 컷이 화면 범위 밖이면 다이아몬드 전용
// 초록 구역 자체가 없음)의 점이 엉뚱하게 진초록으로 칠해져 "점 색=그 점이 놓인 배경 색"이라는
// 원칙이 깨졌다 — 지금은 항상 일치한다). 마스터/다이아몬드는 creator_tier_daily_stats 로 실제
// 구역 곡선을 그릴 수 있는 두 등급이라 색을 넣고, 그 아래 세 등급(플래티넘/골드/실버)은 구역
// 곡선이 없어(컷 미보유) 중립 회색 계열로 통일했다 — 이 셋의 `zone` 값이 모두
// `CREATOR_TIER_NEUTRAL_ZONE_FILL`과 같은 것은 의도된 설계다.
// **(2026-08-30 밤, 마감 품질 수정)** 마스터/다이아몬드가 둘 다 옅은 초록 한 톤이라 실화면에서
// "거의 같은 진초록"으로 읽힌다는 스크린샷 피드백 — 명도 차이만으로는 작은 점·얇은 선에서
// 구분이 약해서, **색상(hue) 자체를 갈랐다**: 마스터는 짙은 초록 계열 그대로 유지하고,
// 다이아몬드는 청록에서 파란 계열("다이아몬드=아이스블루")로 옮겨 나란히 있어도 확실히 다른
// 색으로 보이게 했다. `DailyTrendSparkline`은 이 둘이 인접할 때 경계 곡선에 옅은 흰 실선도
// 추가로 긋는다(아래 컴포넌트 참고) — 회색↔초록(또는 파랑) 경계는 그대로 손대지 않았다.
export const CREATOR_TIER_COLORS = {
  마스터: { zone: "#d7f0e2", point: "#0e6b52" },
  다이아몬드: { zone: "#dcedf7", point: "#2f7fae" },
  플래티넘: { zone: "#f1f0ec", point: "#5c5856" },
  골드: { zone: "#f1f0ec", point: "#7a7670" },
  실버: { zone: "#f1f0ec", point: "#a09c94" },
};

// 등급 구역 배경 중 특정 등급에 속하지 않는 "그 외"(가장 낮은 표시 컷보다 아래, 또는 컷 데이터
// 자체가 없는 경우) 영역의 배경/포인트 색 쌍 — 위 CREATOR_TIER_COLORS 의 하위 3등급 zone 값과
// zone 이 동일해 시각적으로 자연스럽게 이어지고, point 는 그 구역용 대표 회색 하나로 통일한다.
export const CREATOR_TIER_NEUTRAL_ZONE_FILL = "#f1f0ec";
export const CREATOR_TIER_NEUTRAL_POINT_COLOR = "#7a7670";
